import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineFire, HiOutlineRefresh, HiOutlineLightningBolt, HiOutlineGlobe, HiOutlineChartBar, HiOutlineArrowUp, HiOutlineArrowDown, HiOutlineCash } from 'react-icons/hi';
import CountUp from 'react-countup';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { fmt } from '../utils';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import { fetchDegenTrending } from '../api';

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export default function MemecoinsView({ pools, onSelectPool }) {
  useTheme(); // keep hook call for consistency
  const isMobile = useIsMobile();
  const [trendingData, setTrendingData] = useState({ dexScreener: [], geckoTerminal: [] });
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const loadTrending = useCallback(async (silent = false) => {
    if (!silent) setTrendingLoading(true);
    setRefreshing(true);
    try {
      const data = await fetchDegenTrending();
      setTrendingData(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.warn("Failed to load degen trending:", err);
    } finally {
      setTrendingLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial fetch + auto-refresh every 5 min
  useEffect(() => {
    loadTrending();
    intervalRef.current = setInterval(() => loadTrending(true), REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [loadTrending]);

  // Cross-reference GeckoTerminal trending pools with DeFi Llama pools for real APR data
  const enrichedGeckoTerminal = useMemo(() => {
    if (!trendingData.geckoTerminal.length) return [];
    return trendingData.geckoTerminal.map(pool => {
      const baseToken = (pool.baseToken || '').trim().toUpperCase();
      const quoteToken = (pool.quoteToken || '').trim().toUpperCase();
      if (!baseToken || !quoteToken) {
        return { ...pool, apr: pool.aprEstimated || 0, aprSource: pool.aprSource || 'estimated' };
      }
      const poolChain = (pool.chain || '').toLowerCase();
      // Try to find a matching DeFi Llama pool by tokens and chain
      const match = pools.find(p => {
        const llamaTokens = (p.tokens || []).map(t => t.toUpperCase());
        const llamaChain = (p.chain || '').toLowerCase();
        const tokensMatch =
          llamaTokens.some(t => t.includes(baseToken) || baseToken.includes(t)) &&
          llamaTokens.some(t => t.includes(quoteToken) || quoteToken.includes(t));
        // Prefer same-chain match, but also accept token-only match
        if (tokensMatch && poolChain && llamaChain && poolChain === llamaChain) return true;
        return false;
      }) || pools.find(p => {
        // Fallback: token match only (cross-chain)
        const llamaTokens = (p.tokens || []).map(t => t.toUpperCase());
        return (
          llamaTokens.some(t => t.includes(baseToken) || baseToken.includes(t)) &&
          llamaTokens.some(t => t.includes(quoteToken) || quoteToken.includes(t))
        );
      });
      if (match) {
        return { ...pool, apr: match.apr, aprSource: 'defillama' };
      }
      return { ...pool, apr: pool.aprEstimated || 0, aprSource: pool.aprSource || 'estimated' };
    });
  }, [trendingData.geckoTerminal, pools]);

  return (
    <div style={{ background: 'linear-gradient(180deg, #0D0600 0%, #0A0400 100%)', minHeight: '100vh' }}>
    <div style={{ padding: isMobile ? '12px 12px' : '20px 24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Degen Banner */}
      <style>{`
        @keyframes degenGradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes degenGlow {
          0%, 100% { box-shadow: 0 0 30px rgba(255,69,0,0.12), 0 0 80px rgba(255,69,0,0.04); }
          50% { box-shadow: 0 0 40px rgba(255,69,0,0.2), 0 0 100px rgba(255,69,0,0.08); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes accentSlide {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.008 }}
        style={{
          background: 'linear-gradient(135deg, #1C0800 0%, #2A1000 20%, #1F0A02 45%, #18060A 70%, #0E0300 100%)',
          backgroundSize: '300% 300%',
          animation: 'degenGradientShift 12s ease infinite, degenGlow 4s ease-in-out infinite',
          borderRadius: 16,
          padding: isMobile ? '26px 20px' : '36px 40px',
          marginBottom: isMobile ? 20 : 28,
          border: '1px solid rgba(255,69,0,0.2)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated gradient accent line */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: 'linear-gradient(90deg, #FF4500, #FFD700, #FF6B35, #FF4500, #FFD700, #FF6B35)',
          backgroundSize: '200% 100%',
          animation: 'accentSlide 4s linear infinite',
          borderRadius: '16px 16px 0 0',
        }} />

        {/* Subtle dot grid pattern */}
        {!isMobile && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            pointerEvents: 'none',
            backgroundImage: 'radial-gradient(rgba(255,107,53,0.06) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }} />
        )}

        {/* Top label row */}
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: '#FF6B35',
            letterSpacing: 2.5,
            fontWeight: 700,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <HiOutlineFire style={{ fontSize: 15, color: '#FF4500' }} />
          DEGEN ZONE
          <span style={{
            fontSize: 8,
            color: '#FFD700',
            background: 'rgba(255,215,0,0.1)',
            border: '1px solid rgba(255,215,0,0.2)',
            padding: '2px 8px',
            borderRadius: 3,
            fontWeight: 600,
            letterSpacing: 1.2,
          }}>
            HIGH OCTANE
          </span>
        </motion.div>

        {/* Title with text gradient */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.45 }}
          style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: isMobile ? 30 : 46,
            background: 'linear-gradient(135deg, #FF6B35 0%, #FFD700 50%, #FFA726 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: 10,
            lineHeight: 1.05,
            letterSpacing: -0.5,
          }}
        >
          Ape Harder, Farm Smarter
        </motion.div>

        {/* Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: 'rgba(255,255,255,0.45)',
            maxWidth: 540,
            lineHeight: 1.6,
          }}
        >
          Where degens find alpha. Trending pools and hyped tokens across all chains — updated live.
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          style={{
            display: 'flex',
            gap: isMobile ? 14 : 24,
            marginTop: 20,
            flexWrap: 'wrap',
          }}
        >
          {[
            {
              icon: <HiOutlineChartBar style={{ fontSize: 14, color: '#FF6B35' }} />,
              value: enrichedGeckoTerminal.length,
              label: 'Trending Pools',
            },
            {
              icon: <HiOutlineLightningBolt style={{ fontSize: 14, color: '#FFD700' }} />,
              value: trendingData.dexScreener.length,
              label: 'Boosted Tokens',
            },
            {
              icon: <HiOutlineGlobe style={{ fontSize: 14, color: '#FFA726' }} />,
              value: null,
              label: 'Multi-Chain',
            },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.55 + i * 0.08, duration: 0.3 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8,
                padding: '8px 14px',
              }}
            >
              {stat.icon}
              <div>
                <div style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 18,
                  color: '#FFFFFF',
                  lineHeight: 1.1,
                }}>
                  {stat.value !== null ? (
                    <CountUp end={stat.value} duration={1.8} separator="," />
                  ) : (
                    <span style={{ fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>LIVE</span>
                  )}
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: 0.8,
                  marginTop: 1,
                }}>
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Refresh controls */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.65, duration: 0.4 }}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: 'rgba(255,255,255,0.3)',
            marginTop: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#FF4500',
              animation: 'pulse 2s infinite',
              display: 'inline-block',
              boxShadow: '0 0 8px rgba(255,69,0,0.5)',
            }} />
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>Auto-refresh 5m</span>
            <AnimatePresence mode="wait">
              {lastRefresh && (
                <motion.span
                  key={lastRefresh.getTime()}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 4 }}
                  transition={{ duration: 0.25 }}
                  style={{ color: 'rgba(255,255,255,0.2)' }}
                >
                  — {lastRefresh.toLocaleTimeString()}
                </motion.span>
              )}
            </AnimatePresence>
          </span>
          <motion.button
            onClick={() => loadTrending(true)}
            disabled={refreshing}
            whileTap={{ scale: 0.92 }}
            whileHover={{ scale: 1.05 }}
            style={{
              background: 'rgba(255,69,0,0.12)',
              border: '1px solid rgba(255,69,0,0.2)',
              borderRadius: 6,
              cursor: refreshing ? 'default' : 'pointer',
              color: '#FF6B35',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              fontWeight: 600,
              padding: '4px 10px',
              opacity: refreshing ? 0.5 : 1,
              transition: 'all 0.15s ease',
            }}
          >
            <HiOutlineRefresh style={{
              fontSize: 12,
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
            }} />
            Refresh
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Trending Memecoins — GeckoTerminal */}
      {enrichedGeckoTerminal.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ marginBottom: 28 }}
        >
          <Tooltip
            id="gecko-tooltip"
            style={{
              background: 'rgba(20,12,6,0.95)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,69,0,0.25)',
              borderRadius: 8,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: 'rgba(255,255,255,0.85)',
              padding: '10px 14px',
              lineHeight: 1.6,
              maxWidth: 280,
              zIndex: 100,
            }}
          />
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: '#FF6B35',
            letterSpacing: 1.5,
            fontWeight: 700,
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <span style={{ fontSize: 13 }}>🔥</span>
            TRENDING POOLS
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 500,
              letterSpacing: 0.5,
            }}>
              <CountUp end={enrichedGeckoTerminal.length} duration={1.2} /> pools
            </span>
            <span style={{
              fontSize: 8,
              color: '#FF4500',
              background: 'rgba(255,69,0,0.12)',
              border: '1px solid rgba(255,69,0,0.25)',
              padding: '1px 6px',
              borderRadius: 3,
              fontWeight: 600,
              letterSpacing: 1,
            }}>
              LIVE
            </span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: isMobile ? 10 : 14,
          }}>
            {enrichedGeckoTerminal.slice(0, 12).map((pool, i) => {
              const isUp = pool.priceChangeH24 >= 0;
              const totalTxns = (pool.txnsH24Buys || 0) + (pool.txnsH24Sells || 0);
              const buyRatio = totalTxns > 0 ? (pool.txnsH24Buys || 0) / totalTxns * 100 : 50;
              const priceDisplay = pool.priceUsd < 0.01
                ? pool.priceUsd.toExponential(2)
                : pool.priceUsd.toFixed(pool.priceUsd < 1 ? 4 : 2);
              const priceNum = parseFloat(priceDisplay);
              const accentColor = isUp ? '#00E676' : '#FF5252';
              const geckoUrl = (() => {
                if (!pool.id) return '#';
                const parts = pool.id.split('_');
                if (parts.length >= 2) {
                  const network = parts[0];
                  const poolAddress = parts.slice(1).join('_');
                  return `https://www.geckoterminal.com/${network}/pools/${poolAddress}`;
                }
                return '#';
              })();
              return (
                <motion.a
                  key={pool.id || i}
                  href={geckoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.35, type: 'spring', stiffness: 120, damping: 14 }}
                  whileHover={{ scale: 1.03, y: -4 }}
                  style={{
                    background: 'linear-gradient(165deg, rgba(28,16,8,0.85) 0%, rgba(32,20,14,0.9) 40%, rgba(24,16,10,0.85) 100%)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 12,
                    padding: isMobile ? '14px 14px' : '18px 20px',
                    border: '1px solid rgba(255,69,0,0.12)',
                    borderLeft: `3px solid ${accentColor}`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Glassmorphism shimmer overlay */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'radial-gradient(ellipse at 20% 0%, rgba(255,69,0,0.04) 0%, transparent 60%)',
                    pointerEvents: 'none',
                  }} />

                  {/* Row 1: Name + Chain/Dex badges + Rank */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, position: 'relative' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: 16,
                        color: '#FFFFFF',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: 5,
                      }}>
                        {pool.name}
                      </div>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 8,
                          color: 'rgba(255,255,255,0.55)',
                          background: 'rgba(255,255,255,0.07)',
                          padding: '2px 6px',
                          borderRadius: 3,
                          letterSpacing: 0.3,
                        }}>
                          {pool.chain}
                        </span>
                        {pool.dex && (
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 8,
                            color: 'rgba(255,255,255,0.38)',
                            background: 'rgba(255,255,255,0.04)',
                            padding: '2px 6px',
                            borderRadius: 3,
                            letterSpacing: 0.3,
                          }}>
                            {pool.dex}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Flame-colored rank badge */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, rgba(255,69,0,0.25) 0%, rgba(255,140,0,0.18) 100%)',
                      border: '1px solid rgba(255,107,53,0.35)',
                    }}>
                      <span style={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: 14,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #FF6B35, #FFD700)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        lineHeight: 1,
                      }}>
                        {pool.rank}
                      </span>
                    </div>
                  </div>

                  {/* Row 2: Price (CountUp) + 24h change pill */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span
                      style={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: 22,
                        color: '#FFFFFF',
                        letterSpacing: -0.3,
                      }}
                      data-tooltip-id="gecko-tooltip"
                      data-tooltip-content={`Price: $${pool.priceUsd.toPrecision(8)}`}
                    >
                      $<CountUp
                        end={priceNum}
                        decimals={pool.priceUsd < 0.01 ? 6 : (pool.priceUsd < 1 ? 4 : 2)}
                        duration={1.5}
                        separator=","
                        preserveValue
                      />
                    </span>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 13,
                      fontWeight: 700,
                      color: accentColor,
                      background: isUp ? 'rgba(0,230,118,0.12)' : 'rgba(255,82,82,0.12)',
                      border: `1px solid ${isUp ? 'rgba(0,230,118,0.25)' : 'rgba(255,82,82,0.25)'}`,
                      padding: '4px 10px',
                      borderRadius: 20,
                    }}>
                      {isUp
                        ? <HiOutlineArrowUp style={{ fontSize: 13, strokeWidth: 2.5 }} />
                        : <HiOutlineArrowDown style={{ fontSize: 13, strokeWidth: 2.5 }} />
                      }
                      {Math.abs(pool.priceChangeH24).toFixed(1)}%
                    </span>
                  </div>

                  {/* APR Row */}
                  {pool.apr > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 10,
                      padding: '8px 10px',
                      background: pool.apr > 20
                        ? 'rgba(255,215,0,0.08)'
                        : 'rgba(0,230,118,0.08)',
                      border: `1px solid ${pool.apr > 20 ? 'rgba(255,215,0,0.18)' : 'rgba(0,230,118,0.18)'}`,
                      borderRadius: 8,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 9,
                          color: 'rgba(255,255,255,0.4)',
                          letterSpacing: 1,
                          marginBottom: 2,
                          textTransform: 'uppercase',
                        }}>
                          APR
                        </div>
                        <div
                          style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontSize: 22,
                            color: pool.apr > 20 ? '#FFD700' : '#00E676',
                            lineHeight: 1.1,
                          }}
                          data-tooltip-id="gecko-tooltip"
                          data-tooltip-content={
                            pool.aprSource === 'defillama'
                              ? 'Real APR from DeFi Llama'
                              : 'Estimated APR based on 24h volume and pool fees'
                          }
                        >
                          <CountUp
                            end={pool.apr}
                            decimals={2}
                            duration={1.5}
                            preserveValue
                            suffix="%"
                          />
                        </div>
                      </div>
                      {pool.aprSource === 'estimated' && (
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 8,
                          fontWeight: 700,
                          color: '#FFA726',
                          background: 'rgba(255,167,38,0.15)',
                          border: '1px solid rgba(255,167,38,0.3)',
                          padding: '2px 6px',
                          borderRadius: 3,
                          letterSpacing: 0.8,
                        }}>
                          EST
                        </span>
                      )}
                      {pool.aprSource === 'defillama' && (
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 8,
                          fontWeight: 700,
                          color: '#00E676',
                          background: 'rgba(0,230,118,0.15)',
                          border: '1px solid rgba(0,230,118,0.3)',
                          padding: '2px 6px',
                          borderRadius: 3,
                          letterSpacing: 0.8,
                        }}>
                          ✓ REAL
                        </span>
                      )}
                    </div>
                  )}

                  {/* Row 3: 5M / 1H mini changes + FDV */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    {[
                      { label: '5M', value: pool.priceChangeM5 },
                      { label: '1H', value: pool.priceChangeH1 },
                    ].map(({ label, value }) => {
                      const up = value >= 0;
                      return (
                        <span key={label} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 10,
                          color: up ? '#00E676' : '#FF5252',
                        }}>
                          {up
                            ? <HiOutlineArrowUp style={{ fontSize: 10 }} />
                            : <HiOutlineArrowDown style={{ fontSize: 10 }} />
                          }
                          <span style={{ color: 'rgba(255,255,255,0.4)', marginRight: 1 }}>{label}</span>
                          {Math.abs(value).toFixed(1)}%
                        </span>
                      );
                    })}
                    {pool.fdv > 0 && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10,
                        color: 'rgba(255,255,255,0.35)',
                        marginLeft: 'auto',
                      }}>
                        FDV {fmt(pool.fdv)}
                      </span>
                    )}
                  </div>

                  {/* Row 4: Volume + Liquidity */}
                  <div style={{
                    display: 'flex',
                    gap: 16,
                    padding: '10px 0',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    marginBottom: 10,
                  }}>
                    <div
                      style={{ flex: 1 }}
                      data-tooltip-id="gecko-tooltip"
                      data-tooltip-content={`Volume 24h: $${(pool.volumeH24 || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 8,
                        color: 'rgba(255,255,255,0.35)',
                        letterSpacing: 0.8,
                        marginBottom: 3,
                        textTransform: 'uppercase',
                      }}>
                        <HiOutlineChartBar style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }} />
                        Volume 24h
                      </div>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.75)',
                        fontWeight: 600,
                      }}>
                        <CountUp
                          end={pool.volumeH24 || 0}
                          duration={1.8}
                          separator=","
                          prefix="$"
                          formattingFn={(v) => fmt(v)}
                          preserveValue
                        />
                      </div>
                    </div>
                    <div
                      style={{ flex: 1 }}
                      data-tooltip-id="gecko-tooltip"
                      data-tooltip-content={`Liquidity: $${(pool.reserveUsd || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 8,
                        color: 'rgba(255,255,255,0.35)',
                        letterSpacing: 0.8,
                        marginBottom: 3,
                        textTransform: 'uppercase',
                      }}>
                        <HiOutlineCash style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }} />
                        Liquidity
                      </div>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.55)',
                        fontWeight: 600,
                      }}>
                        <CountUp
                          end={pool.reserveUsd || 0}
                          duration={1.8}
                          separator=","
                          prefix="$"
                          formattingFn={(v) => fmt(v)}
                          preserveValue
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 5: Buy/Sell ratio bar */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9,
                    }}>
                      <span style={{ color: '#00E676', fontWeight: 600 }}>
                        B {pool.txnsH24Buys}
                      </span>
                      <span style={{ color: '#FF5252', fontWeight: 600 }}>
                        S {pool.txnsH24Sells}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        height: 4,
                        borderRadius: 2,
                        overflow: 'hidden',
                        background: 'rgba(255,255,255,0.04)',
                      }}
                      data-tooltip-id="gecko-tooltip"
                      data-tooltip-content={`Buys: ${pool.txnsH24Buys} (${buyRatio.toFixed(0)}%) | Sells: ${pool.txnsH24Sells} (${(100 - buyRatio).toFixed(0)}%)`}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${buyRatio}%` }}
                        transition={{ duration: 0.8, delay: i * 0.04 + 0.3, ease: 'easeOut' }}
                        style={{
                          height: '100%',
                          background: 'linear-gradient(90deg, #00C853, #00E676)',
                          borderRadius: '2px 0 0 2px',
                        }}
                      />
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${100 - buyRatio}%` }}
                        transition={{ duration: 0.8, delay: i * 0.04 + 0.3, ease: 'easeOut' }}
                        style={{
                          height: '100%',
                          background: 'linear-gradient(90deg, #FF5252, #FF1744)',
                          borderRadius: '0 2px 2px 0',
                        }}
                      />
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* DexScreener Boosted Tokens */}
      {trendingData.dexScreener.length > 0 && (() => {
        const CHAIN_BADGE_COLORS = {
          Ethereum: '#5470D4', Arbitrum: '#1A8ED4', Optimism: '#D40010',
          Base: '#0040D4', Polygon: '#6B35C8', BSC: '#F0B90B',
          Solana: '#9945FF', Avalanche: '#E84142',
        };
        const tokens20 = trendingData.dexScreener.slice(0, 20);
        const maxBoost = Math.max(...tokens20.map(t => t.boostAmount || 0), 1);
        const boostThreshold = maxBoost * 0.5;
        return (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          style={{ marginBottom: 20 }}
        >
          <Tooltip
            id="dex-tooltip"
            style={{
              background: 'rgba(20,12,6,0.95)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,69,0,0.25)',
              borderRadius: 8,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: 'rgba(255,255,255,0.85)',
              padding: '10px 14px',
              lineHeight: 1.6,
              maxWidth: 280,
              zIndex: 100,
            }}
          />

          {/* Section header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: '#FF6B35',
              letterSpacing: 1.5,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <HiOutlineFire style={{ fontSize: 13, color: '#FF4500' }} />
              MOST HYPED
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: 'rgba(255,255,255,0.5)',
                fontWeight: 500,
                letterSpacing: 0.5,
              }}>
                <CountUp end={tokens20.length} duration={1.2} preserveValue /> tokens
              </span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: 'rgba(255,255,255,0.25)',
                fontWeight: 400,
              }}>/</span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: 'rgba(255,255,255,0.4)',
                fontWeight: 500,
                letterSpacing: 1.5,
              }}>DEXSCREENER</span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 8,
                color: '#FF4500',
                background: 'linear-gradient(135deg, rgba(255,69,0,0.15), rgba(255,140,0,0.1))',
                border: '1px solid rgba(255,69,0,0.3)',
                padding: '2px 8px',
                borderRadius: 4,
                fontWeight: 700,
                letterSpacing: 1.2,
              }}>
                BOOSTED
              </span>
            </div>
          </div>

          {/* Grid layout — same as GeckoTerminal */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: isMobile ? 10 : 14,
          }}>
            {tokens20.map((token, i) => {
              const chainColor = CHAIN_BADGE_COLORS[token.chain] || '#888';
              const isTop3 = i < 3;
              const isHighBoost = (token.boostAmount || 0) > boostThreshold;
              const boostDisplay = token.boostAmount >= 1000
                ? `${(token.boostAmount / 1000).toFixed(1)}K`
                : String(token.boostAmount || 0);
              const hasPrice = token.priceUsd && token.priceUsd > 0;
              const isUp24h = (token.priceChange24h || 0) >= 0;
              const accentColor = isUp24h ? '#00E676' : '#FF5252';
              const priceDisplay = hasPrice
                ? (token.priceUsd < 0.01
                    ? token.priceUsd.toExponential(2)
                    : token.priceUsd.toFixed(token.priceUsd < 1 ? 4 : 2))
                : null;
              const priceNum = priceDisplay ? parseFloat(priceDisplay) : 0;
              const totalTxns = (token.txns24hBuys || 0) + (token.txns24hSells || 0);
              const buyRatio = totalTxns > 0 ? (token.txns24hBuys || 0) / totalTxns * 100 : 50;

              // Simplified card for tokens without price data
              if (!hasPrice) {
                return (
                  <motion.a
                    key={token.tokenAddress || i}
                    href={token.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.35, type: 'spring', stiffness: 120, damping: 14 }}
                    whileHover={{ scale: 1.03, y: -4 }}
                    style={{
                      background: 'linear-gradient(165deg, rgba(28,16,8,0.85) 0%, rgba(32,20,14,0.9) 40%, rgba(24,16,10,0.85) 100%)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: 12,
                      padding: isMobile ? '14px 14px' : '18px 20px',
                      border: '1px solid rgba(255,69,0,0.12)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                    }}
                  >
                    {/* Token icon */}
                    <div style={{ width: 40, height: 40, position: 'relative', flexShrink: 0 }}>
                      {token.icon && (
                        <img src={token.icon} alt="" style={{
                          width: 40, height: 40, borderRadius: '50%',
                          border: '1.5px solid rgba(255,255,255,0.1)',
                          position: 'absolute', top: 0, left: 0, zIndex: 1,
                        }} onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(255,69,0,0.15), rgba(255,140,0,0.1))',
                        border: '1.5px solid rgba(255,255,255,0.08)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: "'DM Serif Display', serif", fontSize: 17, color: 'rgba(255,255,255,0.6)',
                      }}>
                        {(token.symbol || token.name || '?').charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "'DM Serif Display', serif", fontSize: 15, color: '#FFFFFF',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4,
                      }}>
                        {token.symbol || token.name}
                      </div>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fontWeight: 700,
                          color: chainColor, background: `${chainColor}20`, border: `1px solid ${chainColor}40`,
                          padding: '2px 7px', borderRadius: 4, letterSpacing: 0.6, textTransform: 'uppercase',
                        }}>{token.chain}</span>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 3,
                          padding: '2px 8px', borderRadius: 10,
                          background: isHighBoost
                            ? 'linear-gradient(135deg, rgba(255,69,0,0.2), rgba(255,200,0,0.15))'
                            : 'rgba(255,69,0,0.1)',
                          border: isHighBoost ? '1px solid rgba(255,140,0,0.4)' : '1px solid rgba(255,69,0,0.15)',
                        }}>
                          <HiOutlineLightningBolt style={{ fontSize: 8, color: isHighBoost ? '#FFD700' : '#FF6B35' }} />
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fontWeight: 700,
                            color: isHighBoost ? '#FFD700' : '#FF6B35', letterSpacing: 0.5,
                          }}>{boostDisplay}</span>
                        </div>
                      </div>
                    </div>
                    <span style={{
                      fontFamily: "'DM Serif Display', serif", fontSize: 14,
                      color: 'rgba(255,140,0,0.3)', lineHeight: 1, flexShrink: 0,
                    }}>#{i + 1}</span>
                  </motion.a>
                );
              }

              // Rich card for tokens with price data
              return (
                <motion.a
                  key={token.tokenAddress || i}
                  href={token.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.35, type: 'spring', stiffness: 120, damping: 14 }}
                  whileHover={{ scale: 1.03, y: -4 }}
                  style={{
                    background: isTop3
                      ? 'linear-gradient(165deg, #201008 0%, #2A1610 40%, #1E100A 100%)'
                      : 'linear-gradient(165deg, rgba(28,16,8,0.85) 0%, rgba(32,20,14,0.9) 40%, rgba(24,16,10,0.85) 100%)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 12,
                    padding: isMobile ? '14px 14px' : '18px 20px',
                    border: isTop3
                      ? '1.5px solid rgba(255,140,0,0.4)'
                      : '1px solid rgba(255,69,0,0.12)',
                    borderLeft: `3px solid ${accentColor}`,
                    boxShadow: isTop3
                      ? '0 0 20px rgba(255,100,0,0.15), 0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)'
                      : '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Glassmorphism shimmer overlay */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'radial-gradient(ellipse at 20% 0%, rgba(255,69,0,0.04) 0%, transparent 60%)',
                    pointerEvents: 'none',
                  }} />

                  {/* Row 1: Icon + Name + Chain/Dex badges + Rank */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                      {/* Token icon */}
                      <div style={{ width: 40, height: 40, position: 'relative', flexShrink: 0 }}>
                        {token.icon && (
                          <img src={token.icon} alt="" style={{
                            width: 40, height: 40, borderRadius: '50%',
                            border: isTop3 ? '2px solid rgba(255,140,0,0.35)' : '1.5px solid rgba(255,255,255,0.1)',
                            position: 'absolute', top: 0, left: 0, zIndex: 1,
                          }} onError={e => { e.target.style.display = 'none'; }} />
                        )}
                        <div style={{
                          width: 40, height: 40, borderRadius: '50%',
                          background: isTop3
                            ? 'linear-gradient(135deg, rgba(255,69,0,0.2), rgba(255,200,0,0.12))'
                            : 'linear-gradient(135deg, rgba(255,69,0,0.15), rgba(255,140,0,0.1))',
                          border: isTop3 ? '2px solid rgba(255,140,0,0.25)' : '1.5px solid rgba(255,255,255,0.08)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: "'DM Serif Display', serif", fontSize: 17, color: 'rgba(255,255,255,0.6)',
                        }}>
                          {(token.symbol || token.name || '?').charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                          <span style={{
                            fontFamily: "'DM Serif Display', serif", fontSize: 16, color: '#FFFFFF',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {token.symbol || token.name}
                          </span>
                          {isHighBoost && (
                            <HiOutlineFire style={{ fontSize: 12, color: '#FF6B35', flexShrink: 0 }} />
                          )}
                        </div>
                        {token.name && token.symbol && (
                          <div style={{
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                            color: 'rgba(255,255,255,0.35)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            maxWidth: 140,
                          }}>
                            {token.name}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Rank badge */}
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      width: 32, height: 32, borderRadius: 8,
                      background: 'linear-gradient(135deg, rgba(255,69,0,0.25) 0%, rgba(255,140,0,0.18) 100%)',
                      border: '1px solid rgba(255,107,53,0.35)',
                    }}>
                      <span style={{
                        fontFamily: "'DM Serif Display', serif", fontSize: 14, fontWeight: 700,
                        background: 'linear-gradient(135deg, #FF6B35, #FFD700)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1,
                      }}>
                        {i + 1}
                      </span>
                    </div>
                  </div>

                  {/* Row 2: Price + 24h change pill */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span
                      style={{
                        fontFamily: "'DM Serif Display', serif", fontSize: 18, color: '#FFFFFF', letterSpacing: -0.3,
                      }}
                      data-tooltip-id="dex-tooltip"
                      data-tooltip-content={`Price: $${token.priceUsd.toPrecision(8)}`}
                    >
                      $<CountUp
                        end={priceNum}
                        decimals={token.priceUsd < 0.01 ? 6 : (token.priceUsd < 1 ? 4 : 2)}
                        duration={1.5}
                        separator=","
                        preserveValue
                      />
                    </span>
                    <span style={{
                      display: 'flex', alignItems: 'center', gap: 3,
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700,
                      color: accentColor,
                      background: isUp24h ? 'rgba(0,230,118,0.12)' : 'rgba(255,82,82,0.12)',
                      border: `1px solid ${isUp24h ? 'rgba(0,230,118,0.25)' : 'rgba(255,82,82,0.25)'}`,
                      padding: '4px 10px', borderRadius: 20,
                    }}>
                      {isUp24h
                        ? <HiOutlineArrowUp style={{ fontSize: 13, strokeWidth: 2.5 }} />
                        : <HiOutlineArrowDown style={{ fontSize: 13, strokeWidth: 2.5 }} />
                      }
                      {Math.abs(token.priceChange24h || 0).toFixed(1)}%
                    </span>
                  </div>

                  {/* APR Row */}
                  {(token.aprEstimated || 0) > 0 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
                      padding: '8px 10px',
                      background: (token.aprEstimated || 0) > 20 ? 'rgba(255,215,0,0.08)' : 'rgba(0,230,118,0.08)',
                      border: `1px solid ${(token.aprEstimated || 0) > 20 ? 'rgba(255,215,0,0.18)' : 'rgba(0,230,118,0.18)'}`,
                      borderRadius: 8,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                          color: 'rgba(255,255,255,0.4)', letterSpacing: 1, marginBottom: 2, textTransform: 'uppercase',
                        }}>APR</div>
                        <div style={{
                          fontFamily: "'DM Serif Display', serif", fontSize: 22,
                          color: (token.aprEstimated || 0) > 20 ? '#FFD700' : '#00E676', lineHeight: 1.1,
                        }}
                          data-tooltip-id="dex-tooltip"
                          data-tooltip-content={
                            token.aprSource === 'defillama'
                              ? 'Real APR from DeFi Llama'
                              : 'Estimated APR based on 24h volume and pool fees'
                          }
                        >
                          <CountUp end={token.aprEstimated} decimals={2} duration={1.5} preserveValue suffix="%" />
                        </div>
                      </div>
                      {(!token.aprSource || token.aprSource === 'estimated') && (
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fontWeight: 700,
                          color: '#FFA726', background: 'rgba(255,167,38,0.15)',
                          border: '1px solid rgba(255,167,38,0.3)', padding: '2px 6px', borderRadius: 3, letterSpacing: 0.8,
                        }}>EST</span>
                      )}
                      {token.aprSource === 'defillama' && (
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fontWeight: 700,
                          color: '#00E676', background: 'rgba(0,230,118,0.15)',
                          border: '1px solid rgba(0,230,118,0.3)', padding: '2px 6px', borderRadius: 3, letterSpacing: 0.8,
                        }}>REAL</span>
                      )}
                    </div>
                  )}

                  {/* Row 3: 5M / 1H mini changes + FDV/MCap */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    {[
                      { label: '5M', value: token.priceChange5m },
                      { label: '1H', value: token.priceChange1h },
                    ].map(({ label, value }) => {
                      const val = value || 0;
                      const up = val >= 0;
                      return (
                        <span key={label} style={{
                          display: 'flex', alignItems: 'center', gap: 3,
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                          color: up ? '#00E676' : '#FF5252',
                        }}>
                          {up
                            ? <HiOutlineArrowUp style={{ fontSize: 10 }} />
                            : <HiOutlineArrowDown style={{ fontSize: 10 }} />
                          }
                          <span style={{ color: 'rgba(255,255,255,0.4)', marginRight: 1 }}>{label}</span>
                          {Math.abs(val).toFixed(1)}%
                        </span>
                      );
                    })}
                    {(token.fdv || token.marketCap) > 0 && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                        color: 'rgba(255,255,255,0.35)', marginLeft: 'auto',
                      }}>
                        {token.marketCap > 0 ? `MC ${fmt(token.marketCap)}` : `FDV ${fmt(token.fdv)}`}
                      </span>
                    )}
                  </div>

                  {/* Row 4: Volume + Liquidity */}
                  <div style={{
                    display: 'flex', gap: 16, padding: '10px 0',
                    borderTop: '1px solid rgba(255,255,255,0.06)', marginBottom: 10,
                  }}>
                    <div style={{ flex: 1 }}
                      data-tooltip-id="dex-tooltip"
                      data-tooltip-content={`Volume 24h: $${(token.volume24h || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
                        color: 'rgba(255,255,255,0.35)', letterSpacing: 0.8, marginBottom: 3, textTransform: 'uppercase',
                      }}>
                        <HiOutlineChartBar style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }} />
                        Volume 24h
                      </div>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
                        color: 'rgba(255,255,255,0.75)', fontWeight: 600,
                      }}>
                        <CountUp end={token.volume24h || 0} duration={1.8} separator="," prefix="$" formattingFn={(v) => fmt(v)} preserveValue />
                      </div>
                    </div>
                    <div style={{ flex: 1 }}
                      data-tooltip-id="dex-tooltip"
                      data-tooltip-content={`Liquidity: $${(token.liquidity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
                        color: 'rgba(255,255,255,0.35)', letterSpacing: 0.8, marginBottom: 3, textTransform: 'uppercase',
                      }}>
                        <HiOutlineCash style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }} />
                        Liquidity
                      </div>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
                        color: 'rgba(255,255,255,0.55)', fontWeight: 600,
                      }}>
                        <CountUp end={token.liquidity || 0} duration={1.8} separator="," prefix="$" formattingFn={(v) => fmt(v)} preserveValue />
                      </div>
                    </div>
                  </div>

                  {/* Row 5: Buy/Sell ratio bar */}
                  {totalTxns > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', marginBottom: 4,
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                      }}>
                        <span style={{ color: '#00E676', fontWeight: 600 }}>B {token.txns24hBuys}</span>
                        <span style={{ color: '#FF5252', fontWeight: 600 }}>S {token.txns24hSells}</span>
                      </div>
                      <div
                        style={{
                          display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden',
                          background: 'rgba(255,255,255,0.04)',
                        }}
                        data-tooltip-id="dex-tooltip"
                        data-tooltip-content={`Buys: ${token.txns24hBuys} (${buyRatio.toFixed(0)}%) | Sells: ${token.txns24hSells} (${(100 - buyRatio).toFixed(0)}%)`}
                      >
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${buyRatio}%` }}
                          transition={{ duration: 0.8, delay: i * 0.04 + 0.3, ease: 'easeOut' }}
                          style={{ height: '100%', background: 'linear-gradient(90deg, #00C853, #00E676)', borderRadius: '2px 0 0 2px' }}
                        />
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${100 - buyRatio}%` }}
                          transition={{ duration: 0.8, delay: i * 0.04 + 0.3, ease: 'easeOut' }}
                          style={{ height: '100%', background: 'linear-gradient(90deg, #FF5252, #FF1744)', borderRadius: '0 2px 2px 0' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Row 6: Chain + DEX badges + Boost indicator */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap',
                    paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fontWeight: 700,
                      color: chainColor, background: `${chainColor}20`, border: `1px solid ${chainColor}40`,
                      padding: '2px 7px', borderRadius: 4, letterSpacing: 0.6, textTransform: 'uppercase',
                    }}>{token.chain}</span>
                    {token.dex && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
                        color: 'rgba(255,255,255,0.38)', background: 'rgba(255,255,255,0.04)',
                        padding: '2px 6px', borderRadius: 3, letterSpacing: 0.3,
                      }}>{token.dex}</span>
                    )}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 3, marginLeft: 'auto',
                      padding: '2px 8px', borderRadius: 10,
                      background: isHighBoost
                        ? 'linear-gradient(135deg, rgba(255,69,0,0.2), rgba(255,200,0,0.15))'
                        : 'rgba(255,69,0,0.1)',
                      border: isHighBoost ? '1px solid rgba(255,140,0,0.4)' : '1px solid rgba(255,69,0,0.15)',
                    }}>
                      <HiOutlineLightningBolt style={{ fontSize: 8, color: isHighBoost ? '#FFD700' : '#FF6B35' }} />
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fontWeight: 700,
                        color: isHighBoost ? '#FFD700' : '#FF6B35', letterSpacing: 0.5,
                      }}>{boostDisplay}</span>
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </div>
        </motion.div>
        );
      })()}

      {/* Loading state — skeleton pulse */}
      {trendingLoading && enrichedGeckoTerminal.length === 0 && (
        <div style={{ padding: '24px 0' }}>
          {/* Skeleton header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
          }}>
            <motion.div
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 180, height: 14, borderRadius: 6,
                background: 'linear-gradient(90deg, rgba(255,69,0,0.08), rgba(255,69,0,0.15), rgba(255,69,0,0.08))',
              }}
            />
            <motion.div
              animate={{ opacity: [0.2, 0.5, 0.2] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
              style={{
                width: 60, height: 12, borderRadius: 4,
                background: 'rgba(255,69,0,0.08)',
              }}
            />
          </div>
          {/* Skeleton cards row */}
          <div style={{ display: 'flex', gap: 10, overflowX: 'hidden' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <motion.div
                key={i}
                animate={{ opacity: [0.15, 0.35, 0.15] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: i * 0.12 }}
                style={{
                  width: 130, minWidth: 130, height: 160,
                  borderRadius: 12,
                  background: 'linear-gradient(165deg, rgba(255,69,0,0.04) 0%, rgba(255,69,0,0.08) 50%, rgba(255,69,0,0.04) 100%)',
                  border: '1px solid rgba(255,69,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: 16,
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'rgba(255,69,0,0.08)',
                }} />
                <div style={{ width: '70%', height: 10, borderRadius: 4, background: 'rgba(255,69,0,0.06)' }} />
                <div style={{ width: '50%', height: 8, borderRadius: 3, background: 'rgba(255,69,0,0.04)' }} />
                <div style={{ width: '60%', height: 8, borderRadius: 10, background: 'rgba(255,69,0,0.05)' }} />
              </motion.div>
            ))}
          </div>
          {/* Loading text */}
          <div style={{
            textAlign: 'center',
            marginTop: 16,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: 'rgba(255,255,255,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}>
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              style={{ color: '#FF6B35' }}
            >
              Loading
            </motion.span>
            trending memecoins...
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
